import { notifyError } from "@/lib/notify-error";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { GitBranchPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { RevisionBadge } from "@/components/semantic-badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface CreateTakeoffRevisionDialogProps {
  jobId: Id<"takeoffPilotJobs"> | null;
  jobName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (jobId: Id<"takeoffPilotJobs">) => void;
}

function getRevisionLabel(revision: {
  revisionName: string;
  revisionNumber: number;
  originalFilename: string;
}) {
  return `${revision.revisionName} (r${revision.revisionNumber}) - ${revision.originalFilename}`;
}

const MAX_LISTED_SHEETS = 8;

function formatSheetList(pages: Array<{ pageNumber: number; sheetNumber?: string }>) {
  const labels = pages.map((page) => page.sheetNumber ?? `Page ${page.pageNumber}`);
  const listed = labels.slice(0, MAX_LISTED_SHEETS).join(", ");
  return labels.length > MAX_LISTED_SHEETS
    ? `${listed}, +${labels.length - MAX_LISTED_SHEETS} more`
    : listed;
}

const SHEET_INDEX_PENDING_NOTES = {
  indexing: {
    pages: "Sheets in this revision are still being indexed. Try again once indexing finishes.",
    document:
      "Sheets in this revision are still being indexed. Wait to match measurements by sheet number, or they copy by page number.",
  },
  unindexed: {
    pages: "Both revisions need a completed sheet index to match selected pages. Retry indexing if it failed.",
    document: "Sheet numbers aren't indexed for both revisions, so measurements copy by page number.",
  },
};

function SheetIndexPendingNote({
  status,
  isPageSelection,
}: {
  status: keyof typeof SHEET_INDEX_PENDING_NOTES;
  isPageSelection: boolean;
}) {
  const notes = SHEET_INDEX_PENDING_NOTES[status];
  // Selected pages can't move without their sheets; a whole PDF falls back to page numbers.
  return isPageSelection ? (
    <p className="text-xs text-destructive">{notes.pages}</p>
  ) : (
    <p className="text-xs text-muted-foreground">{notes.document}</p>
  );
}

/** How the plan's sheets line up with the chosen revision, by sheet number. */
function PlanSheetMatchSummary({
  sourcePlanDocumentId,
  targetRevisionId,
}: {
  sourcePlanDocumentId: Id<"takeoffPilotPlanDocuments">;
  targetRevisionId: Id<"projectDocumentRevisions">;
}) {
  const { data: match } = useQuery(
    convexQuery(api.takeoffPilot.jobRevisions.previewPlanRevisionMatch, {
      sourcePlanDocumentId,
      targetRevisionId,
    })
  );
  if (!match) {
    return null;
  }
  if (match.status !== "matched") {
    return <SheetIndexPendingNote status={match.status} isPageSelection={match.isPageSelection} />;
  }

  const droppedMeasurements = match.missing.reduce((sum, page) => sum + page.measurementCount, 0);
  return (
    <div className="space-y-1.5 text-xs text-muted-foreground" data-slot="sheet-match-summary">
      <div className="flex flex-wrap gap-1.5">
        <Badge size="sm" variant="outline">
          {match.matchedCount} Matched
        </Badge>
        {match.missing.length > 0 ? (
          <Badge size="sm" variant="outline">
            {match.missing.length} Missing
          </Badge>
        ) : null}
        {match.added.length > 0 ? (
          <Badge size="sm" variant="outline">
            {match.added.length} New
          </Badge>
        ) : null}
      </div>
      {match.missing.length > 0 ? (
        <p>
          Not in this revision: {formatSheetList(match.missing)}.
          {droppedMeasurements > 0
            ? ` ${droppedMeasurements} measurement${droppedMeasurements === 1 ? "" : "s"} on these sheets won't be copied.`
            : null}
        </p>
      ) : null}
      {match.added.length > 0 ? (
        <p>
          New in this revision: {formatSheetList(match.added)}.
          {match.isPageSelection ? " New sheets aren't added to the selected pages." : null}
        </p>
      ) : null}
    </div>
  );
}

function getDefaultChangedRevisionId(
  revisions: Array<{ _id: Id<"projectDocumentRevisions">; isCurrent: boolean }>
) {
  return revisions.filter((revision) => !revision.isCurrent).at(-1)?._id;
}

export function CreateTakeoffRevisionDialog({
  jobId,
  jobName,
  open,
  onOpenChange,
  onCreated,
}: CreateTakeoffRevisionDialogProps) {
  const createRevision = useMutation(api.takeoffPilot.jobRevisions.createRevisionFromJob);
  const { data: options, isPending, isError, error, refetch } = useQuery(
    convexQuery(
      api.takeoffPilot.jobRevisions.getRevisionOptionsForJob,
      open && jobId ? { jobId } : "skip"
    )
  );
  const [name, setName] = useState("");
  const [scheduleRevisionId, setScheduleRevisionId] = useState("");
  const [selectedPlanRevisions, setSelectedPlanRevisions] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initializedJobIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      initializedJobIdRef.current = null;
      return;
    }
    if (!options) {
      return;
    }
    const sourceJobId = String(options.sourceJob._id);
    if (initializedJobIdRef.current === sourceJobId) {
      return;
    }
    initializedJobIdRef.current = sourceJobId;

    setName(`${options.sourceJob.name} R${options.nextRevisionNumber}`);
    setScheduleRevisionId(
      options.scheduleDocument ? String(options.scheduleDocument.currentRevisionId) : ""
    );
    setSelectedPlanRevisions({});
  }, [open, options]);

  const selectedScheduleRevision = options?.scheduleDocument?.revisions.find(
    (revision) => String(revision._id) === scheduleRevisionId
  );
  const scheduleReady = !selectedScheduleRevision ||
    selectedScheduleRevision.isCurrent ||
    selectedScheduleRevision.hasCompletedScheduleExtraction;
  const selectedPlanEntries = useMemo(
    () => Object.entries(selectedPlanRevisions).filter(([, revisionId]) => revisionId.length > 0),
    [selectedPlanRevisions]
  );
  const canSubmit = Boolean(
    jobId &&
      options &&
      name.trim() &&
      (!options.scheduleDocument || scheduleRevisionId) &&
      scheduleReady &&
      selectedPlanEntries.length > 0 &&
      !isSubmitting
  );
  const loadErrorMessage = error instanceof Error ? error.message : "Failed to load revision options";

  const handleTogglePlan = (sourcePlanDocumentId: string, checked: boolean) => {
    setSelectedPlanRevisions((current) => {
      const next = { ...current };
      if (!checked) {
        delete next[sourcePlanDocumentId];
        return next;
      }

      const planDocument = options?.planDocuments.find(
        (plan) => String(plan.sourcePlanDocumentId) === sourcePlanDocumentId
      );
      const defaultRevisionId = planDocument
        ? getDefaultChangedRevisionId(planDocument.revisions)
        : undefined;
      if (defaultRevisionId) {
        next[sourcePlanDocumentId] = String(defaultRevisionId);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!jobId) {
      return;
    }
    if (selectedPlanEntries.length === 0) {
      toast.error("Select at least one changed plan revision");
      return;
    }
    if (!scheduleReady) {
      toast.error("Selected schedule revision is not ready for TakeoffPilot yet");
      return;
    }

    setIsSubmitting(true);
    try {
      const createdJobId = await createRevision({
        sourceJobId: jobId,
        name: name.trim(),
        ...(options?.scheduleDocument && scheduleRevisionId
          ? { scheduleRevisionId: scheduleRevisionId as Id<"projectDocumentRevisions"> }
          : {}),
        planRevisionSelections: selectedPlanEntries.map(([sourcePlanDocumentId, targetRevisionId]) => ({
          sourcePlanDocumentId: sourcePlanDocumentId as Id<"takeoffPilotPlanDocuments">,
          targetRevisionId: targetRevisionId as Id<"projectDocumentRevisions">,
        })),
      });
      toast.success("Takeoff revision started");
      onOpenChange(false);
      onCreated(createdJobId);
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to create takeoff revision");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranchPlus className="size-5" />
            Create Takeoff Revision
          </DialogTitle>
          <DialogDescription>
            Copy measurements from {jobName ?? "this job"} onto changed revisions of the same source PDFs.
            Measurements follow their sheets by sheet number.
          </DialogDescription>
        </DialogHeader>

        {isPending ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : isError || !options ? (
          <div className="space-y-3 py-6">
            <p className="text-sm text-destructive">{loadErrorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="space-y-5 overflow-y-auto pe-1">
            <div className="space-y-2">
              <label className="text-sm font-semibold" htmlFor="takeoff-revision-name">
                Revision Name
              </label>
              <Input
                id="takeoff-revision-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={isSubmitting}
              />
            </div>

            {options.scheduleDocument ? (
              <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">Schedule Revision</p>
                    <p className="text-xs text-muted-foreground">
                      {options.scheduleDocument.documentName}
                    </p>
                  </div>
                  {options.scheduleDocument.currentRevisionNumber !== undefined ? (
                    <RevisionBadge number={options.scheduleDocument.currentRevisionNumber} current />
                  ) : null}
                </div>
                <Select
                  value={scheduleRevisionId}
                  onValueChange={setScheduleRevisionId}
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select schedule revision" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.scheduleDocument.revisions.map((revision) => (
                      <SelectItem key={revision._id} value={String(revision._id)}>
                        {getRevisionLabel(revision)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!scheduleReady ? (
                  <p className="text-xs text-destructive">
                    This schedule revision is not ready yet. Wait for schedule extraction to complete.
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="space-y-1 rounded-lg border bg-muted/20 p-3">
                <p className="text-sm font-semibold">No Schedule Attached</p>
                <p className="text-xs text-muted-foreground">
                  This revision will stay custom-item-only and copy custom columns and measurements onto the selected plan revisions.
                </p>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold">Changed Plan Revisions</p>
                <p className="text-xs text-muted-foreground">
                  Select only the source plan PDFs that changed for this revision.
                </p>
              </div>
              <div className="space-y-2">
                {options.planDocuments.map((planDocument) => {
                  const sourcePlanDocumentId = String(planDocument.sourcePlanDocumentId);
                  const changedRevisions = planDocument.revisions.filter((revision) => !revision.isCurrent);
                  const targetRevisionId = selectedPlanRevisions[sourcePlanDocumentId];
                  const isSelected = targetRevisionId !== undefined;
                  return (
                    <div
                      key={sourcePlanDocumentId}
                      className={cn(
                        "rounded-lg border p-3 transition-colors",
                        isSelected ? "border-primary/40 bg-primary/5" : "bg-card"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isSelected}
                          disabled={changedRevisions.length === 0 || isSubmitting}
                          onCheckedChange={(checked) =>
                            handleTogglePlan(sourcePlanDocumentId, checked === true)
                          }
                          aria-label={`Include changed revision for ${planDocument.documentName}`}
                          className="mt-1"
                        />
                        <div className="min-w-0 flex-1 space-y-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold">
                                {planDocument.documentName}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Current: {planDocument.currentRevisionName} (r{planDocument.currentRevisionNumber})
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {planDocument.selectedPageCount !== undefined ? (
                                <Badge size="sm" variant="outline">
                                  {planDocument.selectedPageCount} Selected{" "}
                                  {planDocument.selectedPageCount === 1 ? "Page" : "Pages"}
                                </Badge>
                              ) : null}
                              <Badge size="sm" variant="outline">
                                {changedRevisions.length} Alternate{changedRevisions.length === 1 ? "" : "s"}
                              </Badge>
                            </div>
                          </div>
                          <Select
                            value={targetRevisionId ?? ""}
                            onValueChange={(value) =>
                              setSelectedPlanRevisions((current) => ({
                                ...current,
                                [sourcePlanDocumentId]: value,
                              }))
                            }
                            disabled={!isSelected || isSubmitting || changedRevisions.length === 0}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select changed revision" />
                            </SelectTrigger>
                            <SelectContent>
                              {changedRevisions.map((revision) => (
                                <SelectItem key={revision._id} value={String(revision._id)}>
                                  {getRevisionLabel(revision)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {targetRevisionId ? (
                            <PlanSheetMatchSummary
                              sourcePlanDocumentId={planDocument.sourcePlanDocumentId}
                              targetRevisionId={targetRevisionId as Id<"projectDocumentRevisions">}
                            />
                          ) : null}
                          {changedRevisions.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              No alternate plan revisions are available for this source PDF.
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : null}
            Create Revision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
